/**
 * MediaUpload.tsx
 * Task pane panel for a Media Upload activity. Students snap or pick a photo
 * on their device; it uploads to Firebase Storage and appears in the teacher's
 * live results gallery. v1 is image-only.
 */

import React, { useState, useEffect } from 'react';
import { Check, Timer, Star, Camera } from 'lucide-react';
import { clsx } from 'clsx';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { saveSlideConfig, loadSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import type { MediaUploadConfig } from '@types-local/quiz.types';

export default function MediaUpload(): React.ReactElement {
  const [prompt, setPrompt] = useState('');
  const [timeLimit, setTimeLimit] = useState(120);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(10);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  // Repopulate from the current slide's saved config on return.
  useEffect(() => {
    if (!isOfficeReady) return;
    let cancelled = false;
    (async () => {
      const cfg = loadSlideConfig(await getCurrentSlideIndex());
      if (cancelled || cfg?.type !== 'media_upload') return;
      setPrompt(cfg.prompt);
      setTimeLimitEnabled(cfg.timeLimit !== null);
      if (cfg.timeLimit !== null) setTimeLimit(cfg.timeLimit);
      setMaxFileSizeMb(cfg.maxFileSizeMb);
    })();
    return () => { cancelled = true; };
  }, [isOfficeReady]);

  const handleSave = async () => {
    if (!prompt.trim()) { setError('Add a prompt telling students what to capture.'); return; }
    setError('');
    setSaving(true);
    try {
      const config: MediaUploadConfig = {
        type: 'media_upload',
        prompt: prompt.trim(),
        acceptedTypes: ['image'], // v1: image only
        timeLimit: timeLimitEnabled ? timeLimit : null,
        maxFileSizeMb,
      };
      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_QUIZ_MEDIA_${slideIndex.toString().padStart(2, '0')}`;
      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(buildShapeDescriptor(config, shapeName), slideIndex);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('[Media] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Could not add to the slide. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-base">📸</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Media Upload</h2>
          <p className="text-[10px] text-slate-500">Students snap or upload a photo from their device</p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Prompt</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="Take a photo of your completed lab setup…" rows={3}
          className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-brand-500 transition-colors" />
      </div>

      <div className="flex items-center gap-2 text-[11px] text-blue-700 bg-blue-500/10 border border-blue-500/20 rounded-xl px-3 py-2">
        <Camera size={14} className="shrink-0" />
        <span>Students will see a camera / photo picker. Photos upload to your class gallery in Results.</span>
      </div>

      {/* Settings */}
      <div className="space-y-2.5 pt-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</p>

        {/* Timer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Timer size={13} className="text-slate-500" />
            <span className="text-xs text-slate-700">Time Limit</span>
          </div>
          <div className="flex items-center gap-2">
            {timeLimitEnabled && (
              <input type="number" min={10} max={600} value={timeLimit}
                onChange={(e) => setTimeLimit(Number(e.target.value))}
                className="w-14 bg-surface-elevated border border-surface-border rounded-lg px-2 py-1 text-xs text-slate-900 text-center focus:outline-none focus:border-brand-500" />
            )}
            <button onClick={() => setTimeLimitEnabled((v) => !v)}
              className={clsx('w-10 h-5 rounded-full transition-all', timeLimitEnabled ? 'bg-brand-500' : 'bg-surface-elevated border border-surface-border')}>
              <div className={clsx('w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform', timeLimitEnabled ? 'translate-x-2.5' : '-translate-x-2.5')} />
            </button>
          </div>
        </div>

        {/* Max file size */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star size={13} className="text-yellow-400" />
            <span className="text-xs text-slate-700">Max Photo Size</span>
          </div>
          <div className="flex items-center gap-1">
            {[5, 10, 20].map((v) => (
              <button key={v} onClick={() => setMaxFileSizeMb(v)}
                className={clsx('px-2.5 h-7 rounded-lg text-xs font-bold transition-all', maxFileSizeMb === v ? 'bg-blue-500 text-white' : 'bg-surface-elevated text-slate-500 hover:text-slate-800')}>
                {v}MB
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
      )}

      <Button fullWidth size="md" onClick={handleSave} loading={saving} disabled={!isOfficeReady}
        variant={saved ? 'secondary' : 'primary'} icon={saved ? <Check size={14} /> : undefined}>
        {saved ? 'Saved to Slide!' : 'Add to Slide'}
      </Button>
    </div>
  );
}
